import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { useState } from 'react';

const SITE_URL = import.meta.env.VITE_SITE_URL ?? 'https://greenlyelectronicsrecycling.com';

const loc = {
  name: 'Greenly Electronics Recycling — W 3rd Street',
  shortName: 'W 3rd Street',
  address: '2542 W. 3rd Street',
  city: 'Los Angeles',
  state: 'CA',
  zip: '90057',
  neighborhood: 'Westlake / MacArthur Park / Koreatown',
  phone: '(213) 600-1000',
  tel: '+12136001000',
  email: 'info@greenly.us',
  lat: 34.064,
  lng: -118.274,
  hours: [
    { days: 'Monday – Friday', time: '8:00 AM – 5:00 PM' },
    { days: 'Saturday', time: '9:00 AM – 2:00 PM' },
    { days: 'Sunday', time: 'Closed' },
  ],
  mapEmbed: 'https://maps.google.com/maps?q=Greenly+Electronics+Recycling+%26+Data+Destruction,+2542+W+3rd+St,+Los+Angeles,+CA+90057&output=embed&z=16',
  mapLink: 'https://maps.app.goo.gl/1GFb4ZnoPCREHhzy6',
  nearbyAreas: ['Downtown LA', 'Koreatown', 'Silver Lake', 'Echo Park', 'MacArthur Park', 'Mid-City', 'Westlake', 'Historic Filipinotown'],
  image: '/images/hero-west-3rd-street.jpg',
  heroImage: '/images/hero-west-3rd-street.jpg',
};

const acceptedItems = [
  { icon: 'ri-tv-line', label: 'TVs & Monitors', path: '/tv-recycling' },
  { icon: 'ri-computer-line', label: 'Computers & Laptops', path: '/computer-recycling' },
  { icon: 'ri-battery-charge-line', label: 'All Batteries', path: '/battery-recycling' },
  { icon: 'ri-hard-drive-line', label: 'Hard Drives', path: '/hard-drive-destruction' },
  { icon: 'ri-smartphone-line', label: 'Phones & Tablets', path: '/services' },
  { icon: 'ri-printer-line', label: 'Printers & Fax', path: '/services' },
  { icon: 'ri-router-line', label: 'Network Equipment', path: '/services' },
  { icon: 'ri-keyboard-line', label: 'Keyboards & Mice', path: '/services' },
  { icon: 'ri-server-line', label: 'Servers & Drives', path: '/services' },
  { icon: 'ri-usb-line', label: 'Cables & Adapters', path: '/services' },
];

const faqs = [
  { q: 'Where exactly is the West 3rd Street recycling drop-off?', a: 'Our W 3rd Street location is at 2542 W. 3rd Street, Los Angeles, CA 90057 in the Westlake / MacArthur Park neighborhood. It is easily accessible from the 101 Freeway and is about 5 minutes from Downtown Los Angeles and Koreatown.' },
  { q: 'Do I need an appointment to drop off e-waste at this location?', a: 'No appointment is needed for drop-offs at our W 3rd Street location. Simply bring your electronics during our open hours: Monday through Friday 8:00 AM to 5:00 PM, and Saturday 9:00 AM to 2:00 PM. We are closed on Sundays.' },
  { q: 'Is there parking available at the W 3rd Street location?', a: 'Yes, free parking is available on-site at our W 3rd Street location. We have a designated drop-off area where you can pull up, and our staff will help unload your items from your vehicle.' },
  { q: 'What types of electronics can I recycle here near Koreatown?', a: 'We accept virtually all consumer and commercial electronics at this location including TVs, computers, laptops, printers, phones, tablets, batteries, cables, servers, hard drives, and small appliances. All drop-offs are free for residents.' },
  { q: 'How far is this location from Downtown LA and Echo Park?', a: 'Our W 3rd Street location is approximately 5 minutes from Downtown Los Angeles, 8 minutes from Echo Park, and 7 minutes from Koreatown by car. It is one of the most centrally located e-waste drop-off points in the city.' },
  { q: 'Do you offer pickup service near this location?', a: 'Yes, we offer free pickup for qualifying businesses within 5 miles of this location, including Downtown LA, Koreatown, Silver Lake, Echo Park, and MacArthur Park. Residential pickup is available for a small fee depending on volume and distance.' },
];

const West3rdStreetPage = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${SITE_URL}/locations/west-3rd-street#webpage`,
        url: `${SITE_URL}/locations/west-3rd-street`,
        name: 'Electronics Recycling West 3rd Street Los Angeles | Greenly Drop-Off',
        description: 'Free electronics recycling drop-off at 2542 W. 3rd Street, Los Angeles, CA 90057. Open Mon–Sat. TVs, computers, batteries accepted. Near Koreatown, Downtown LA, Echo Park. No appointment needed.',
        isPartOf: { '@id': `${SITE_URL}/#website` },
        about: { '@id': `${SITE_URL}/#business` },
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
            { '@type': 'ListItem', position: 2, name: 'Drop-Off Locations', item: `${SITE_URL}/locations` },
            { '@type': 'ListItem', position: 3, name: 'W 3rd Street', item: `${SITE_URL}/locations/west-3rd-street` },
          ],
        },
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${SITE_URL}/locations/west-3rd-street#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
          { '@type': 'ListItem', position: 2, name: 'Drop-Off Locations', item: `${SITE_URL}/locations` },
          { '@type': 'ListItem', position: 3, name: 'W 3rd Street', item: `${SITE_URL}/locations/west-3rd-street` },
        ],
      },
      {
        '@type': 'LocalBusiness',
        '@id': `${SITE_URL}/locations/west-3rd-street#localbusiness`,
        name: loc.name,
        description: 'Electronics recycling drop-off facility on W 3rd Street in Los Angeles. Free TV recycling, computer recycling, battery recycling, and hard drive destruction. CalRecycle CEWD-127531 certified.',
        url: `${SITE_URL}/locations/west-3rd-street`,
        telephone: '+12136001000',
        email: 'info@greenly.us',
        image: loc.image,
        address: {
          '@type': 'PostalAddress',
          streetAddress: loc.address,
          addressLocality: loc.city,
          addressRegion: loc.state,
          postalCode: loc.zip,
          addressCountry: 'US',
        },
        geo: { '@type': 'GeoCoordinates', latitude: loc.lat, longitude: loc.lng },
        openingHoursSpecification: [
          { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], opens: '08:00', closes: '17:00' },
          { '@type': 'OpeningHoursSpecification', dayOfWeek: 'Saturday', opens: '09:00', closes: '14:00' },
        ],
        hasMap: loc.mapLink,
        areaServed: [
          { '@type': 'City', name: 'Los Angeles' },
          { '@type': 'Neighborhood', name: 'Westlake' },
          { '@type': 'Neighborhood', name: 'MacArthur Park' },
          { '@type': 'Neighborhood', name: 'Koreatown' },
          { '@type': 'Neighborhood', name: 'Downtown Los Angeles' },
          { '@type': 'Neighborhood', name: 'Silver Lake' },
          { '@type': 'Neighborhood', name: 'Echo Park' },
          { '@type': 'Neighborhood', name: 'Historic Filipinotown' },
          { '@type': 'Neighborhood', name: 'Mid-City' },
        ],
        priceRange: 'Free',
        parentOrganization: { '@id': `${SITE_URL}/#business` },
        sameAs: [
          'https://maps.app.goo.gl/1GFb4ZnoPCREHhzy6',
        ],
      },
      {
        '@type': 'FAQPage',
        '@id': `${SITE_URL}/locations/west-3rd-street#faq`,
        mainEntity: faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.q,
          acceptedAnswer: { '@type': 'Answer', text: faq.a },
        })),
      },
    ],
  };

  return (
    <>
      <title>Electronics Recycling West 3rd Street Los Angeles | Greenly Drop-Off</title>
      <meta name="description" content="Free electronics recycling drop-off at 2542 W. 3rd Street, Los Angeles, CA 90057. Open Mon–Sat. TVs, computers, batteries accepted. Near Koreatown, Downtown LA, Echo Park. No appointment needed. CalRecycle certified." />
      <meta name="keywords" content="electronics recycling near me, e-waste drop off West 3rd Street, electronics recycling Koreatown, recycling center near Downtown LA, computer recycling near me Los Angeles, TV recycling Westlake, free e-waste drop off 90057" />
      <meta name="robots" content="index, follow" />
      <meta name="last-modified" content="2026-05-11" />
      <link rel="canonical" href={`${SITE_URL}/locations/west-3rd-street`} />
      {/* Hyper-local geo meta tags */}
      <meta name="geo.region" content="US-CA" />
      <meta name="geo.placename" content="Los Angeles, California 90057" />
      <meta name="geo.position" content={`${loc.lat};${loc.lng}`} />
      <meta name="ICBM" content={`${loc.lat}, ${loc.lng}`} />
      {/* Open Graph */}
      <meta property="og:title" content="Electronics Recycling West 3rd Street Los Angeles | Greenly Drop-Off" />
      <meta property="og:description" content="Free e-waste drop-off at 2542 W. 3rd Street, LA 90057. Near Koreatown, Downtown LA, Echo Park. TVs, computers, batteries accepted. Open Mon–Sat." />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={`${SITE_URL}/locations/west-3rd-street`} />
      <meta property="og:image" content={loc.image} />
      <meta property="og:site_name" content="Greenly Electronics Recycling" />
      <meta property="og:locale" content="en_US" />
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Electronics Recycling West 3rd Street Los Angeles | Greenly Drop-Off" />
      <meta name="twitter:description" content="Free e-waste drop-off at 2542 W. 3rd Street, LA 90057. Near Koreatown, Downtown LA, Echo Park. TVs, computers, batteries accepted. Open Mon–Sat." />
      <meta name="twitter:image" content={loc.image} />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative bg-gray-900 pt-24 pb-16 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={loc.heroImage}
              alt={`Electronics recycling drop-off near ${loc.neighborhood}, Los Angeles`}
              className="w-full h-full object-cover object-center opacity-30"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-gray-900 via-gray-900/80 to-transparent" />
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[
              { label: 'Home', path: '/' },
              { label: 'Drop-Off Locations', path: '/locations' },
              { label: loc.shortName },
            ]} />
            <div className="max-w-2xl mt-8">
              <div className="inline-flex items-center gap-2 bg-[#1B6B3A] text-white text-xs font-bold px-3 py-1.5 rounded-full mb-4">
                <i className="ri-map-pin-2-fill"></i>
                {loc.neighborhood} — Los Angeles, CA
              </div>
              <h1 className="text-3xl md:text-5xl font-extrabold text-white leading-tight mb-5">
                Electronics Recycling Drop-Off on <span className="text-[#74C69D]">West 3rd Street</span>
              </h1>
              <p className="text-gray-300 text-base md:text-lg leading-relaxed mb-6">
                Free e-waste recycling at <strong className="text-white">2542 W. 3rd Street, Los Angeles, CA 90057</strong>. Drop off TVs, computers, batteries, and all electronics — no appointment needed. Conveniently located near Koreatown, Downtown LA, and Echo Park.
              </p>

              {/* Quick badges */}
              <div className="flex flex-wrap gap-3 mb-6">
                {[
                  { icon: 'ri-time-line', text: 'Mon–Fri 8am–5pm · Sat 9am–2pm' },
                  { icon: 'ri-gift-line', text: 'Free Drop-Off' },
                  { icon: 'ri-award-line', text: 'CalRecycle CEWD-127531' },
                ].map((b) => (
                  <div key={b.text} className="flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 text-white text-xs font-semibold">
                    <i className={`${b.icon} text-[#74C69D]`}></i>{b.text}
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-3">
                <a
                  href={loc.mapLink}
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="inline-flex items-center gap-2 bg-[#1B6B3A] hover:bg-[#2D8653] text-white font-bold px-6 py-3 rounded-md transition-colors cursor-pointer whitespace-nowrap text-sm"
                >
                  <i className="ri-map-pin-fill"></i>Get Directions
                </a>
                <a
                  href={`tel:${loc.tel}`}
                  className="inline-flex items-center gap-2 bg-white/10 border border-white/30 text-white font-semibold px-6 py-3 rounded-md hover:bg-white/20 transition-colors cursor-pointer whitespace-nowrap text-sm"
                >
                  <i className="ri-phone-fill"></i>{loc.phone}
                </a>
                <Link
                  to="/locations/slauson-ave"
                  className="inline-flex items-center gap-2 bg-white/10 border border-white/30 text-white font-semibold px-6 py-3 rounded-md hover:bg-white/20 transition-colors cursor-pointer whitespace-nowrap text-sm"
                >
                  <i className="ri-map-pin-2-line"></i>Our Other Location
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Info + Map split */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-10">
              {/* Info column */}
              <div className="w-full lg:w-2/5 space-y-8">
                {/* Address card */}
                <div className="bg-[#F4FAF7] rounded-2xl p-6 border border-[#1B6B3A]/10">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#1B6B3A] rounded-xl shrink-0">
                      <i className="ri-map-pin-2-fill text-white text-lg"></i>
                    </div>
                    <div>
                      <div className="font-extrabold text-gray-900 text-sm">Drop-Off Address</div>
                      <div className="text-[#1B6B3A] text-xs font-semibold mt-0.5">{loc.neighborhood}</div>
                    </div>
                  </div>
                  <div className="text-gray-700 text-sm font-semibold mb-1">{loc.address}</div>
                  <div className="text-gray-500 text-xs mb-1">{loc.city}, {loc.state} {loc.zip}</div>
                  <a
                    href={loc.mapLink}
                    target="_blank"
                    rel="nofollow noreferrer"
                    className="inline-flex items-center gap-1.5 text-[#1B6B3A] font-semibold text-xs hover:underline cursor-pointer mt-2"
                  >
                    <i className="ri-direction-line"></i>Open in Google Maps
                  </a>
                </div>

                {/* Hours card */}
                <div className="bg-white border border-gray-200 rounded-2xl p-6">
                  <div className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-4">Hours of Operation</div>
                  <div className="space-y-3">
                    {loc.hours.map((h) => (
                      <div key={h.days} className="flex items-center justify-between text-sm">
                        <span className="text-gray-600 font-medium">{h.days}</span>
                        <span className={`font-bold ${h.time === 'Closed' ? 'text-red-400' : 'text-[#1B6B3A]'}`}>{h.time}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Contact card */}
                <div className="bg-white border border-gray-200 rounded-2xl p-6">
                  <div className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-4">Contact</div>
                  <div className="space-y-3">
                    <a href={`tel:${loc.tel}`} className="flex items-center gap-3 text-sm text-gray-700 hover:text-[#1B6B3A] transition-colors cursor-pointer">
                      <div className="w-8 h-8 flex items-center justify-center bg-[#E8F5EE] rounded-lg shrink-0">
                        <i className="ri-phone-line text-[#1B6B3A] text-sm"></i>
                      </div>
                      {loc.phone}
                    </a>
                    <a href={`mailto:${loc.email}`} className="flex items-center gap-3 text-sm text-gray-700 hover:text-[#1B6B3A] transition-colors cursor-pointer">
                      <div className="w-8 h-8 flex items-center justify-center bg-[#E8F5EE] rounded-lg shrink-0">
                        <i className="ri-mail-line text-[#1B6B3A] text-sm"></i>
                      </div>
                      {loc.email}
                    </a>
                  </div>
                </div>

                {/* Nearby areas */}
                <div className="bg-white border border-gray-200 rounded-2xl p-6">
                  <div className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-4">Serving Nearby Neighborhoods</div>
                  <div className="flex flex-wrap gap-2">
                    {loc.nearbyAreas.map((area) => (
                      <span key={area} className="bg-[#F4FAF7] text-[#1B6B3A] text-xs font-semibold px-3 py-1.5 rounded-full">
                        {area}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Map column */}
              <div className="w-full lg:w-3/5">
                <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
                  <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                    <div className="font-extrabold text-gray-900 text-sm">Find Us on the Map</div>
                    <a
                      href={loc.mapLink}
                      target="_blank"
                      rel="nofollow noreferrer"
                      className="text-[#1B6B3A] text-xs font-bold hover:underline cursor-pointer"
                    >
                      <i className="ri-external-link-line mr-1"></i>View Larger Map
                    </a>
                  </div>
                  <iframe
                    src={loc.mapEmbed}
                    title={`Google Map - ${loc.name}`}
                    width="100%"
                    height="480"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="w-full"
                  />
                  <div className="px-5 py-4 bg-[#F4FAF7] border-t border-gray-100">
                    <div className="flex flex-col sm:flex-row gap-3">
                      <a
                        href={loc.mapLink}
                        target="_blank"
                        rel="nofollow noreferrer"
                        className="inline-flex items-center justify-center gap-2 bg-[#1B6B3A] hover:bg-[#2D8653] text-white font-bold px-5 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-sm flex-1"
                      >
                        <i className="ri-direction-line"></i>Get Directions
                      </a>
                      <Link
                        to="/locations/slauson-ave"
                        className="inline-flex items-center justify-center gap-2 border border-gray-200 hover:border-[#1B6B3A] text-gray-700 hover:text-[#1B6B3A] font-semibold px-5 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-sm flex-1"
                      >
                        <i className="ri-map-pin-2-line"></i>See Our Slauson Ave Location
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* What to drop off */}
        <section className="py-14 bg-[#F4FAF7]">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <div className="text-[#1B6B3A] text-xs font-bold uppercase tracking-widest mb-2">What We Accept</div>
              <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">
                What Can I Drop Off at Our West 3rd Street Location?
              </h2>
              <p className="text-gray-500 text-sm mt-2 max-w-xl mx-auto">
                We accept virtually all consumer and commercial electronics at our W 3rd Street location — for free, no appointment needed.
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
              {acceptedItems.map((item) => (
                <Link
                  key={item.label}
                  to={item.path}
                  className="flex flex-col items-center gap-2 bg-white hover:bg-[#E8F5EE] border border-transparent hover:border-[#1B6B3A] rounded-xl p-4 text-center transition-colors cursor-pointer"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-[#F4FAF7] rounded-xl">
                    <i className={`${item.icon} text-[#1B6B3A] text-lg`}></i>
                  </div>
                  <span className="text-xs font-bold text-gray-700">{item.label}</span>
                </Link>
              ))}
            </div>
            <div className="text-center">
              <Link
                to="/faq"
                className="inline-flex items-center gap-2 text-[#1B6B3A] font-bold text-sm hover:underline cursor-pointer"
              >
                <i className="ri-question-line"></i>See the Full List of Items We Recycle
              </Link>
            </div>
          </div>
        </section>

        {/* How to drop off */}
        <section className="py-14 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <div className="text-[#1B6B3A] text-xs font-bold uppercase tracking-widest mb-2">Simple Process</div>
              <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">
                How Drop-Off Works at Our West 3rd Street Location
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[
                { num: '01', icon: 'ri-box-3-line', title: 'Gather Your Items', desc: 'Collect all your unwanted electronics — TVs, computers, phones, batteries, cables, anything with a cord or battery.' },
                { num: '02', icon: 'ri-car-line', title: 'Drive to Our Facility', desc: 'Head to 2542 W. 3rd Street, Los Angeles, CA 90057. Free parking is available on-site at our designated drop-off area.' },
                { num: '03', icon: 'ri-hand-heart-line', title: 'We Handle the Rest', desc: 'Our staff helps unload your items, checks them in, and gives you a receipt. No paperwork needed — just drop and go.' },
                { num: '04', icon: 'ri-file-list-3-line', title: 'Get Your Certificate', desc: 'Receive a Certificate of Recycling via email. For hard drives and data devices, request a Certificate of Destruction as well.' },
              ].map((step) => (
                <div key={step.num} className="bg-[#F4FAF7] rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#1B6B3A] rounded-xl shrink-0">
                      <i className={`${step.icon} text-white text-lg`}></i>
                    </div>
                    <span className="text-3xl font-extrabold text-gray-200">{step.num}</span>
                  </div>
                  <h3 className="font-extrabold text-gray-900 text-sm mb-2">{step.title}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Location photo */}
        <section className="bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-6 pb-14">
            <img
              src={loc.image}
              alt={`Electronics recycling drop-off facility at ${loc.address}, ${loc.city}`}
              className="w-full h-64 md:h-80 object-cover object-top rounded-2xl"
              loading="lazy"
            />
          </div>
        </section>

        {/* FAQ */}
        <section className="py-14 bg-[#F4FAF7]">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <div className="text-[#1B6B3A] text-xs font-bold uppercase tracking-widest mb-2">Common Questions</div>
              <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">
                West 3rd Street Location FAQs
              </h2>
            </div>
            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <div key={i} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                  <button
                    className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  >
                    <span className="text-sm font-bold text-gray-900">{faq.q}</span>
                    <div className="w-5 h-5 flex items-center justify-center shrink-0">
                      <i className={`text-[#1B6B3A] transition-transform duration-200 ${openFaq === i ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
                    </div>
                  </button>
                  {openFaq === i && (
                    <div className="px-5 pb-4">
                      <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Cross-link to other location */}
        <section className="py-10 bg-white border-t border-gray-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-6">
              <div className="text-[#1B6B3A] text-xs font-bold uppercase tracking-widest mb-2">Our Other Location</div>
              <h2 className="text-lg font-extrabold text-gray-900">We Also Have a Drop-Off in South LA</h2>
            </div>
            <div className="max-w-2xl mx-auto">
              <Link
                to="/locations/slauson-ave"
                className="flex items-center gap-5 bg-[#F4FAF7] hover:bg-[#E8F5EE] border border-transparent hover:border-[#1B6B3A] rounded-2xl p-6 transition-colors cursor-pointer group"
              >
                <div className="w-14 h-14 flex items-center justify-center bg-[#b45309] rounded-xl shrink-0">
                  <i className="ri-map-pin-2-fill text-white text-2xl"></i>
                </div>
                <div className="flex-1">
                  <div className="font-extrabold text-gray-900 text-sm">Greenly Electronics Recycling — W Slauson Ave</div>
                  <div className="text-gray-500 text-xs mt-0.5">4902 W. Slauson Ave, Los Angeles, CA 90056</div>
                  <div className="text-gray-400 text-xs mt-0.5">Ladera Heights / Baldwin Hills · Mon–Fri 8am–5pm · Sat 9am–2pm</div>
                </div>
                <i className="ri-arrow-right-line text-gray-400 group-hover:text-[#1B6B3A] group-hover:translate-x-1 transition-all"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Related service links */}
        <section className="py-10 bg-[#F4FAF7]">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
            <div className="text-[#1B6B3A] text-xs font-bold uppercase tracking-widest mb-3">Related Services</div>
            <h2 className="text-lg font-extrabold text-gray-900 mb-5">Learn More About Our Recycling Services</h2>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                { label: 'TV Recycling', path: '/tv-recycling' },
                { label: 'Computer Recycling', path: '/computer-recycling' },
                { label: 'Battery Recycling', path: '/battery-recycling' },
                { label: 'Hard Drive Destruction', path: '/hard-drive-destruction' },
                { label: 'EV Charger Recycling', path: '/ev-charging-recycling' },
                { label: 'Business Sustainability', path: '/sustainability-report' },
              ].map((s) => (
                <Link
                  key={s.path}
                  to={s.path}
                  className="flex items-center gap-2 bg-white border border-gray-200 hover:border-[#1B6B3A] text-gray-700 hover:text-[#1B6B3A] px-4 py-2.5 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
                >
                  {s.label}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-14 bg-[#1B6B3A]">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-4">
              Ready to Drop Off Your E-Waste?
            </h2>
            <p className="text-green-100 text-sm md:text-base mb-8 max-w-xl mx-auto">
              Visit us at 2542 W. 3rd Street, Los Angeles, CA 90057 — no appointment needed. Open Monday through Saturday. Can&apos;t make it? We offer free pickup for qualifying businesses.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href={loc.mapLink}
                target="_blank"
                rel="nofollow noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-white text-[#1B6B3A] font-bold px-8 py-3 rounded-md hover:bg-green-50 transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                <i className="ri-map-pin-fill"></i>Get Directions
              </a>
              <Link
                to="/pickup-request"
                className="inline-flex items-center justify-center gap-2 bg-white/20 border border-white/40 text-white font-semibold px-8 py-3 rounded-md hover:bg-white/30 transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                <i className="ri-truck-line"></i>Request Free Pickup
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 bg-white/20 border border-white/40 text-white font-semibold px-8 py-3 rounded-md hover:bg-white/30 transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                <i className="ri-mail-line"></i>Contact Us
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default West3rdStreetPage;